import { SendUnlessConditionType } from '@provision/conversation-types';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { DraftScheduleSendUnless } from 'src/pages/Conversations/Chat/conversation-draft-schedule';

export interface SendUnlessOption {
  type: DraftScheduleSendUnless['type'];
  label: string;
  selected: boolean;
}

interface UseSendUnlessConditionsResult {
  options: SendUnlessOption[];
  selectedConditions: DraftScheduleSendUnless[];
  toggleCondition: (type: DraftScheduleSendUnless['type']) => void;
  hydrate: (source?: DraftScheduleSendUnless[] | null) => void;
}

export const SEND_UNLESS_LABELS: Record<DraftScheduleSendUnless['type'], string> = {
  [SendUnlessConditionType.AgentReply]: 'Agent replies',
  [SendUnlessConditionType.ClientReply]: 'Client Replies',
  [SendUnlessConditionType.StatusChange]: 'Status changes',
  [SendUnlessConditionType.AssigneeChange]: 'Assignee changes',
};

const CONDITION_TYPES = Object.keys(SEND_UNLESS_LABELS) as DraftScheduleSendUnless['type'][];

export function getSendUnlessLabel(type: DraftScheduleSendUnless['type']): string {
  return SEND_UNLESS_LABELS[type];
}

export function useSendUnlessConditions(
  initial?: DraftScheduleSendUnless[] | null,
): UseSendUnlessConditionsResult {
  const [selectedConditions, setSelectedConditions] = useState<DraftScheduleSendUnless[]>([]);
  const lastKnownRef = useRef(
    new Map<DraftScheduleSendUnless['type'], DraftScheduleSendUnless>(),
  );

  const hydrate = useCallback((source?: DraftScheduleSendUnless[] | null): void => {
    const map = lastKnownRef.current;
    map.clear();

    if (!source?.length) {
      setSelectedConditions([]);
      return;
    }

    const sanitized = source
      .filter((item): item is DraftScheduleSendUnless => Boolean(item?.type))
      .map((item) => ({ ...item }));

    sanitized.forEach((item) => {
      map.set(item.type, item);
    });

    setSelectedConditions(sanitized);
  }, []);

  useEffect(() => {
    hydrate(initial);
  }, [hydrate, initial]);

  const toggleCondition = useCallback((type: DraftScheduleSendUnless['type']): void => {
    setSelectedConditions((prev) => {
      const map = lastKnownRef.current;
      const existing = prev.find((item) => item.type === type);

      if (existing) {
        map.set(type, existing);
        return prev.filter((item) => item.type !== type);
      }

      const stored = map.get(type) ?? { type };
      const next = { ...stored };
      map.set(type, next);
      return [...prev, next];
    });
  }, []);

  const options = useMemo<SendUnlessOption[]>(() => {
    const set = new Set(selectedConditions.map((item) => item.type));
    return CONDITION_TYPES.map((type) => ({
      type,
      label: SEND_UNLESS_LABELS[type],
      selected: set.has(type),
    }));
  }, [selectedConditions]);

  return {
    options,
    selectedConditions,
    toggleCondition,
    hydrate,
  };
}
